import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { User, Settings, Shield, LogOut } from "lucide-react";
import { PermissionsMatrix } from "@/components/permissions/PermissionsMatrix";
import RefineWindroseMetrics from "@/components/dashboard/SpreadsheetOverlay";
import { useAuthContext } from '@/contexts/BetterAuthContext';
import { useNavigate } from "react-router-dom";

export function UserMenu() {
  const { user, session, signOut } = useAuthContext();
  const navigate = useNavigate();

  // Use actual user data, then session metadata, then defaults
  const currentUser = {
    name: user?.full_name || session?.user?.user_metadata?.full_name || "User",
    email: user?.email || session?.user?.email || "user@company.com",
    role: user?.role || "User",
    avatar: null,
    permissions: ["manage_users", "approve_agent_overrides", "view_audit_logs"]
  };

  const hasPermission = (permission: string) => {
    return currentUser.permissions.includes(permission);
  };

  const canManagePermissions = hasPermission("manage_users");
  const canRefineMetrics = hasPermission("approve_agent_overrides");

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full hover:bg-accent transition-colors">
          <Avatar className="h-8 w-8">
            <AvatarImage src={currentUser.avatar || ""} alt={currentUser.name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {currentUser.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-background border shadow-lg" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{currentUser.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {currentUser.email}
            </p>
            <div className="pt-1">
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                {currentUser.role}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={() => navigate('/account')}
        >
          <User className="mr-2 h-4 w-4" />
          <span>Account</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild className="cursor-pointer hover:bg-accent transition-colors">
          <a href="/settings">
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </a>
        </DropdownMenuItem>

        {canManagePermissions && (
          <Dialog>
            <DialogTrigger asChild>
              <DropdownMenuItem 
                className="cursor-pointer hover:bg-accent transition-colors"
                onSelect={(e) => e.preventDefault()}
              >
                <Shield className="mr-2 h-4 w-4" />
                <span>Permissions</span>
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Permissions Management</DialogTitle>
              </DialogHeader>
              <PermissionsMatrix />
            </DialogContent>
          </Dialog>
        )}

        {canRefineMetrics && (
          <Dialog>
            <DialogTrigger asChild>
              <DropdownMenuItem 
                className="cursor-pointer hover:bg-accent transition-colors"
                onSelect={(e) => e.preventDefault()}
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Refine Windrose Metrics</span>
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Refine Windrose Metrics</DialogTitle>
              </DialogHeader>
              <RefineWindroseMetrics />
            </DialogContent>
          </Dialog>
        )}

        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}